import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { firstVisibleHousePath, resolvePanelHomePath } from '@/application/navigation/menuHouses';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import {
  DATA_FLOOR,
  defaultReportPeriod,
  defaultReportPeriodValue,
  parseDashboardPeriod,
  persistGlobalPeriod,
  previousApiPeriodQuery,
  readMetricsScope,
  yearMonthCoverage,
  type ApiPeriodQuery,
  type MetricsGranularity,
} from '@/application/metrics/metricsScope';

export interface PeriodOption {
  value: string;
  label: string;
  year: number;
  month: number;
  granularity: MetricsGranularity;
}

export interface GlobalPeriodValue {
  /** `YYYY-MM` (mes) o `YYYY` (vista anual). */
  period: string;
  setPeriod: (value: string) => void;
  /** Año/mes resueltos (en vista anual: último mes cubierto, para APIs solo-mes). */
  year: number;
  month: number;
  granularity: MetricsGranularity;
  /** Mes único o rango YTD/anual para APIs que agregan. */
  periodQuery: ApiPeriodQuery;
  /** Baseline: mes anterior, o el mismo tramo del año previo. */
  previousPeriodQuery: ApiPeriodQuery;
  periodOptions: PeriodOption[];
  label: string;
}

const GlobalPeriodContext = createContext<GlobalPeriodValue | null>(null);

/** Opciones mes a mes desde el mes actual hacia DATA_FLOOR, con entrada anual por año. */
export function buildGlobalPeriodOptions(language: string): PeriodOption[] {
  const locale = language || 'es';
  const date = new Date();
  date.setDate(1);
  const options: PeriodOption[] = [];
  let lastYear: number | null = null;
  while (
    date.getFullYear() > DATA_FLOOR.year ||
    (date.getFullYear() === DATA_FLOOR.year && date.getMonth() + 1 >= DATA_FLOOR.month)
  ) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    if (lastYear !== year) {
      lastYear = year;
      options.push({
        value: String(year),
        label: String(year),
        year,
        month: yearMonthCoverage(year).toMonth,
        granularity: 'year',
      });
    }
    const value = `${year}-${String(month).padStart(2, '0')}`;
    const label = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
    options.push({
      value,
      label: label.charAt(0).toUpperCase() + label.slice(1),
      year,
      month,
      granularity: 'month',
    });
    date.setMonth(date.getMonth() - 1);
  }
  return options;
}

function periodFromSearchParams(params: URLSearchParams): string | undefined {
  const period = params.get('period');
  if (period && parseDashboardPeriod(period)) {
    return period;
  }
  const year = params.get('year');
  const month = params.get('month');
  if (year && month) {
    const value = `${year}-${String(Number(month)).padStart(2, '0')}`;
    if (parseDashboardPeriod(value)) {
      return value;
    }
  }
  if (year && parseDashboardPeriod(year)) {
    return year;
  }
  return undefined;
}

function initialPeriod(params: URLSearchParams): string {
  const fromUrl = periodFromSearchParams(params);
  if (fromUrl) {
    return fromUrl;
  }
  const shared = readMetricsScope();
  if (shared.dashboardPeriod && parseDashboardPeriod(shared.dashboardPeriod)) {
    return shared.dashboardPeriod;
  }
  if (shared.reportPeriod && parseDashboardPeriod(shared.reportPeriod)) {
    return shared.reportPeriod;
  }
  return defaultReportPeriodValue();
}

/**
 * Contrato FASE 1 — un solo periodo de métricas en toda la app.
 *
 * Cómo consumir:
 *   const { period, setPeriod, year, month, periodQuery, periodOptions } = useGlobalPeriod();
 *   // periodQuery (rango en vista anual) va a dashboard/summary; year/month a APIs solo-mes.
 *   // No crear otro useState de mes ni otro selector: el chip vive en MainShell.
 *
 * Persistencia: localStorage (`panelclinicas.metricsScope`) + query `period`
 * (también `year` y `month` resueltos). Prioridad al cargar: URL → localStorage → mes cerrado anterior.
 * Al navegar, el chip reescribe `period`/`year`/`month` sin tocar `tenant` ni el resto de params.
 */
export function GlobalPeriodProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = useRef(location.search);
  searchRef.current = location.search;
  const { hasPermission } = usePermissions();
  const periodOptions = useMemo(() => buildGlobalPeriodOptions(i18n.language), [i18n.language]);
  const fallback = defaultReportPeriod();
  const defaultPeriodValue = defaultReportPeriodValue();

  const [period, setPeriodState] = useState(() => initialPeriod(params));

  const urlPeriod = periodFromSearchParams(params);
  useEffect(() => {
    if (urlPeriod) {
      setPeriodState((current) => (current === urlPeriod ? current : urlPeriod));
    }
  }, [urlPeriod]);

  const selected =
    periodOptions.find((option) => option.value === period) ??
    periodOptions.find((option) => option.value === defaultPeriodValue) ??
    periodOptions[0];

  const parsed = parseDashboardPeriod(selected?.value ?? period) ?? parseDashboardPeriod(defaultPeriodValue);
  const year = parsed?.year ?? selected?.year ?? fallback.year;
  const month = parsed?.month ?? selected?.month ?? fallback.month;
  const granularity: MetricsGranularity = parsed?.granularity ?? selected?.granularity ?? 'month';
  const periodQuery = useMemo((): ApiPeriodQuery => {
    if (granularity === 'year') {
      const { fromMonth, toMonth } = yearMonthCoverage(year);
      return { year, fromMonth, toMonth };
    }
    return { year, month };
  }, [granularity, year, month]);
  const prevPeriodQuery = useMemo(() => previousApiPeriodQuery(periodQuery), [periodQuery]);
  const resolvedPeriod = selected?.value ?? parsed?.dashboardPeriod ?? defaultPeriodValue;
  const label = selected?.label ?? formatPeriodTitle(i18n.language, year, month);
  const rootHome = location.pathname === '/' ? firstVisibleHousePath(hasPermission) : null;
  const reportsHome = location.pathname === '/reports' ? resolvePanelHomePath(hasPermission) : null;

  useEffect(() => {
    persistGlobalPeriod(resolvedPeriod);

    const yearStr = String(year);
    const monthStr = String(month);
    const applyPeriod = (prev: URLSearchParams) => {
      const next = new URLSearchParams(prev);
      let changed = false;
      if (next.get('period') !== resolvedPeriod) {
        next.set('period', resolvedPeriod);
        changed = true;
      }
      if (next.get('year') !== yearStr) {
        next.set('year', yearStr);
        changed = true;
      }
      if (next.get('month') !== monthStr) {
        next.set('month', monthStr);
        changed = true;
      }
      return { next, changed };
    };

    /** Pegar `/reports` o `/` sin Resumen: un salto a la casa con el periodo. */
    const leaveTo = reportsHome || (rootHome && rootHome !== '/' ? rootHome : null);
    if (leaveTo) {
      const { next } = applyPeriod(new URLSearchParams(searchRef.current));
      navigate({ pathname: leaveTo, search: `?${next.toString()}` }, { replace: true });
      return;
    }

    setParams(
      (prev) => {
        const { next, changed } = applyPeriod(prev);
        return changed ? next : prev;
      },
      { replace: true }
    );
  }, [resolvedPeriod, year, month, setParams, location.pathname, rootHome, reportsHome, navigate]);

  const setPeriod = useCallback((value: string) => {
    if (parseDashboardPeriod(value)) {
      setPeriodState(value);
    }
  }, []);

  const value = useMemo<GlobalPeriodValue>(
    () => ({
      period: resolvedPeriod,
      setPeriod,
      year,
      month,
      granularity,
      periodQuery,
      previousPeriodQuery: prevPeriodQuery,
      periodOptions,
      label,
    }),
    [resolvedPeriod, setPeriod, year, month, granularity, periodQuery, prevPeriodQuery, periodOptions, label]
  );

  return createElement(GlobalPeriodContext.Provider, { value }, children);
}

export function useGlobalPeriod(): GlobalPeriodValue {
  const context = useContext(GlobalPeriodContext);
  if (!context) {
    throw new Error('useGlobalPeriod debe usarse dentro de GlobalPeriodProvider (MainShell).');
  }
  return context;
}

export function previousMonthOf(year: number, month: number): { year: number; month: number } {
  if (month <= 1) {
    return { year: year - 1, month: 12 };
  }
  return { year, month: month - 1 };
}

export function formatPeriodTitle(language: string, year: number, month: number): string {
  const date = new Date(year, month - 1, 1);
  const label = new Intl.DateTimeFormat(language, { month: 'long', year: 'numeric' }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}
